import { useBillingStore } from '@/stores/billingStore';
import { CheckoutSlideOver } from '@/components/billing/CheckoutSlideOver';
import { Check, X, Sparkles } from 'lucide-react';
import { useState } from 'react';

const comparisonRows = [
  { capability: 'Zoom Q&A (knowledge base)', free: true, pro: true },
  { capability: 'Portal path for any setting', free: true, pro: true },
  { capability: 'Step-by-step guides', free: true, pro: true },
  { capability: 'Connect your Zoom account', free: false, pro: true },
  { capability: 'Account-specific answers', free: false, pro: true },
  { capability: 'Adoption score & health check', free: false, pro: true },
  { capability: 'License utilization report', free: false, pro: true },
  { capability: 'Natural language configuration', free: false, pro: true },
  { capability: 'Bulk changes across users/groups', free: false, pro: true },
  { capability: 'Before/after audit trail', free: false, pro: true },
  { capability: 'PDF export', free: false, pro: true },
];

export function PricingPage() {
  const billingEnabled = useBillingStore((s) => s.billingEnabled);
  const plan = useBillingStore((s) => s.plan);
  const createCheckout = useBillingStore((s) => s.createCheckout);
  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [annual, setAnnual] = useState(false);

  const isPro = plan === 'pro';

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const secret = await createCheckout(0, annual);
      if (secret) {
        setCheckoutSecret(secret);
        setCheckoutOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-foreground mb-2 text-center">Plans & Pricing</h1>
        <p className="text-muted-foreground text-center mb-6">
          Free to ask. Pro to connect and configure.
        </p>

        {/* Annual/Monthly toggle */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className={`text-sm font-medium ${!annual ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
          <button
            onClick={() => setAnnual(!annual)}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${annual ? 'bg-primary' : 'bg-secondary'} disabled:opacity-50`}
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${annual ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          <span className={`text-sm font-medium ${annual ? 'text-foreground' : 'text-muted-foreground'}`}>
            Annual <span className="text-green-400 text-xs font-normal">(save 10%)</span>
          </span>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Free card */}
          <div className={`rounded-xl border p-6 ${!isPro ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}>
            <h2 className="text-xl font-semibold mb-1">Free</h2>
            <p className="text-sm text-muted-foreground mb-4">Ask anything about Zoom</p>
            <div className="mb-4">
              <span className="text-3xl font-bold">$0</span>
              <span className="text-muted-foreground">/mo</span>
            </div>
            {!isPro && (
              <div className="text-sm text-primary font-medium mb-4">Current plan</div>
            )}
            <ul className="space-y-2">
              {['Zoom Q&A from knowledge base', 'Admin portal paths', 'Step-by-step guides', 'Release notes & updates'].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-green-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro card */}
          <div className={`rounded-xl border-2 p-6 relative ${isPro ? 'border-primary/30 bg-primary/5' : 'border-primary'}`}>
            <h2 className="text-xl font-semibold mb-1">Pro</h2>
            <p className="text-sm text-muted-foreground mb-4">Connect, report, and configure</p>
            <div className="mb-1">
              {annual ? (
                <>
                  <span className="text-3xl font-bold">$540</span>
                  <span className="text-muted-foreground">/yr</span>
                  <span className="text-sm text-muted-foreground ml-1">($45/mo)</span>
                  <span className="text-muted-foreground"> + $10.80/seat/yr</span>
                </>
              ) : (
                <>
                  <span className="text-3xl font-bold">$50</span>
                  <span className="text-muted-foreground">/mo + $1/seat</span>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-4">14-day free trial included</p>
            {!billingEnabled ? (
              <div className="text-sm text-muted-foreground mb-4">
                Paid plans aren't open yet. Every Pro feature is available to you now.
              </div>
            ) : isPro ? (
              <div className="text-sm text-primary font-medium mb-4">Current plan</div>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors mb-4 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {loading ? 'Loading...' : 'Start Free Trial'}
              </button>
            )}
            <p className="text-xs text-muted-foreground mb-2">Everything in Free, plus:</p>
            <ul className="space-y-2">
              {['Connect your Zoom account', 'Account-specific answers', 'Health check & adoption score', 'License utilization report', 'Natural language configuration', 'Bulk changes across users', 'Before/after audit trail'].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-green-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Comparison table */}
        <h2 className="text-lg font-semibold text-foreground mb-4 text-center">Compare plans</h2>
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-card">
                <th className="text-left py-3 px-4 font-medium text-foreground">Feature</th>
                <th className="text-center py-3 px-4 font-medium text-foreground w-24">Free</th>
                <th className="text-center py-3 px-4 font-medium text-primary w-24">Pro</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.capability} className={i < comparisonRows.length - 1 ? 'border-b border-border/50' : ''}>
                  <td className="py-2.5 px-4 text-muted-foreground">{row.capability}</td>
                  <td className="text-center py-2.5 px-4">
                    {row.free ? <Check className="h-4 w-4 text-green-400 inline" /> : <X className="h-4 w-4 text-muted-foreground/30 inline" />}
                  </td>
                  <td className="text-center py-2.5 px-4">
                    {row.pro ? <Check className="h-4 w-4 text-green-400 inline" /> : <X className="h-4 w-4 text-muted-foreground/30 inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CheckoutSlideOver
        open={checkoutOpen}
        clientSecret={checkoutSecret}
        onClose={() => { setCheckoutOpen(false); setCheckoutSecret(null); }}
      />
    </div>
  );
}
